import { useCallback, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Renders the warning icon + a destructive (red) confirm button. */
  destructive?: boolean;
}

/**
 * CSR-1182 — imperative, promise-based confirmation using the AMS design-system
 * AlertDialog instead of the native `window.confirm()` popup. Mirrors the look
 * of the existing destructive modals (e.g. DeleteNoteConfirmModal).
 *
 * Usage — render `confirmDialog` once in the component and await `confirm()`:
 *
 *   const { confirm, confirmDialog } = useConfirm();
 *   async function handleRemove() {
 *     if (!(await confirm({ title: "Remove", description: "...", destructive: true }))) return;
 *     // ...run the mutation
 *   }
 *   return (<>{confirmDialog} ...</>);
 *
 * Confirm resolves `true`; Cancel, Escape and outside-click resolve `false`.
 */
export function useConfirm() {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((result: boolean) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    setOptions(opts);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  // Idempotent: the first caller (button click OR onOpenChange) wins, so the
  // close that follows a confirm/cancel click can't double-resolve the promise.
  const settle = useCallback((result: boolean) => {
    setOpen(false);
    resolverRef.current?.(result);
    resolverRef.current = null;
  }, []);

  const confirmDialog = (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) settle(false);
      }}
    >
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {options?.destructive && (
              <AlertTriangle className="w-5 h-5 text-destructive" aria-hidden="true" />
            )}
            {options?.title}
          </AlertDialogTitle>
          {options?.description && (
            <AlertDialogDescription>{options.description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="confirm-dialog-cancel"
            onClick={() => settle(false)}
          >
            {options?.cancelLabel ?? "Cancel"}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-dialog-confirm"
            onClick={() => settle(true)}
            className={
              options?.destructive
                ? cn(buttonVariants({ variant: "destructive" }))
                : undefined
            }
          >
            {options?.confirmLabel ?? "Confirm"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirm, confirmDialog };
}
